import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  useGooglePlacesSearch,
  type GooglePlaceDetails,
  type GooglePlacePrediction,
} from '@/lib/hooks/useGooglePlacesSearch';
import { usePlacesAutocomplete } from '@/lib/hooks/usePlacesAutocomplete';
import { cn } from '@/lib/utils';
import { Building2, Check, Loader2, MapPin, Sparkles, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CountrySelect } from './CountrySelect';
import type { FieldRendererProps, PartyValue } from './types';
import {
  getAddressFormat,
  getNestedValue,
  parseCompositeValue,
  resolveTemplateVariables,
} from './utils';
import { useTranslation } from 'react-i18next';
import { useDynamicForm } from '../DynamicFormContext';

const DEFAULT_PARTY: PartyValue = {
  name: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  placeId: undefined,
};

/**
 * AI Suggestion Status Badge - shows status only
 */
function AISuggestionStatusBadge({
  suggestionKey,
  enrichmentContext,
  currentValue,
}: {
  suggestionKey: string;
  enrichmentContext?: Record<string, unknown>;
  currentValue: unknown;
}) {
  if (!suggestionKey) return null;

  if (!enrichmentContext || Object.keys(enrichmentContext).length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full 
          bg-[hsl(var(--globe-grey))]/10 text-globe-grey"
        title="AI suggestion will appear after completing the previous step"
      >
        <Sparkles className="h-3 w-3 opacity-50" aria-hidden="true" />
        <span className="opacity-70">AI</span>
      </span>
    );
  }

  const suggestedValue = getNestedValue(enrichmentContext, suggestionKey);

  if (suggestedValue === undefined || suggestedValue === null) {
    return null;
  }

  const currentParty = parseCompositeValue<PartyValue>(currentValue, DEFAULT_PARTY);
  const suggestedParty = parseCompositeValue<PartyValue>(suggestedValue, DEFAULT_PARTY);

  // Check if all fields match
  const fieldsMatch =
    currentParty.name === suggestedParty.name &&
    currentParty.street === suggestedParty.street &&
    currentParty.city === suggestedParty.city &&
    currentParty.state === suggestedParty.state &&
    currentParty.postalCode === suggestedParty.postalCode &&
    currentParty.country === suggestedParty.country;

  if (fieldsMatch) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full 
          bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
      >
        <Check className="h-3 w-3" />
        <span>Standard applied</span>
      </span>
    );
  }

  // No badge shown when suggestion is available but not applied
  return null;
}

/**
 * Party Field Renderer - Name + Address composite field
 * Stores: { name, street, city, state, postalCode, country, placeId? }
 *
 * Google Places API integration:
 * - Search by business name to autocomplete all fields
 * - placeId stores Google Place ID for reference
 * - Address autocomplete when typing street address
 */
export function PartyField({
  field,
  value,
  onChange,
  error,
  enrichmentContext,
  formData,
}: FieldRendererProps) {
  const showSuggestion = field.aiSuggestionEnabled && field.aiSuggestionKey;
  const { t } = useTranslation();

  const resolvedLabel = resolveTemplateVariables(field.label, formData, enrichmentContext);
  const resolvedHelpText = resolveTemplateVariables(field.helpText, formData, enrichmentContext);

  // Parse composite value
  const partyValue = parseCompositeValue<PartyValue>(value, DEFAULT_PARTY);
  const { detectedCountry } = useDynamicForm();

  const format = getAddressFormat(partyValue.country || '');

  // Google Places hooks
  const {
    predictions,
    loading: businessLoading,
    searchBusinesses,
    getPlaceDetails,
    clearPredictions,
  } = useGooglePlacesSearch(300);
  const {
    suggestions: addressSuggestions,
    loading: addressLoading,
    fetchSuggestions: fetchAddressSuggestions,
    clearSuggestions: clearAddressSuggestions,
  } = usePlacesAutocomplete('address');

  // State for UI
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<GooglePlaceDetails | null>(null);
  const [showPreviewCard, setShowPreviewCard] = useState(false);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [streetFocused, setStreetFocused] = useState(false);

  // Refs for click outside detection
  const nameInputRef = useRef<HTMLDivElement>(null);
  const streetInputRef = useRef<HTMLDivElement>(null);

  // Determine if this looks like a business or person based on label
  const isBusinessLikely = /company|business|employer|organization|corp|firm|entity/i.test(
    field.label
  );

  const updateField = (fieldName: keyof PartyValue, fieldValue: string | undefined) => {
    onChange(field.name, { ...partyValue, [fieldName]: fieldValue });
  };

  // Handle business name input changes
  const handleBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    updateField('name', newName);

    // Search for businesses if it looks like a business field and name is long enough
    if (isBusinessLikely && newName.length >= 2) {
      searchBusinesses(newName);
      setShowBusinessDropdown(true);
    } else {
      clearPredictions();
      setShowBusinessDropdown(false);
    }
  };

  // Handle business selection
  const handleBusinessSelect = async (prediction: GooglePlacePrediction) => {
    setShowBusinessDropdown(false);

    // Get full place details
    const details = await getPlaceDetails(prediction.place_id);
    if (details) {
      setSelectedBusiness(details);
      setShowPreviewCard(true);
    }
  };

  // Apply address from preview card
  const handleApplyAddress = () => {
    if (selectedBusiness) {
      const address = selectedBusiness.address;
      onChange(field.name, {
        name: selectedBusiness.name || partyValue.name || '',
        street: address.street || '',
        city: address.city || '',
        state: address.state || '',
        postalCode: address.postalCode || '',
        country: address.countryCode || address.country || '',
        placeId: selectedBusiness.place_id,
      });
      setShowPreviewCard(false);
      setSelectedBusiness(null);
      clearPredictions();
    }
  };

  // Dismiss preview card
  const handleDismissPreview = () => {
    setShowPreviewCard(false);
    setSelectedBusiness(null);
  };

  // Handle street address input changes
  const handleStreetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStreet = e.target.value;
    updateField('street', newStreet);

    // Fetch address suggestions if user is typing
    if (newStreet.length >= 3) {
      fetchAddressSuggestions(newStreet);
      setShowAddressDropdown(true);
    } else {
      clearAddressSuggestions();
      setShowAddressDropdown(false);
    }
  };

  // Handle address suggestion selection
  const handleAddressSelect = async (suggestion: { place_id: string; description: string }) => {
    setShowAddressDropdown(false);
    clearAddressSuggestions();

    // Fetch full address details from Google Places
    if (suggestion.place_id) {
      const details = await getPlaceDetails(suggestion.place_id);
      if (details) {
        const address = details.address;
        // Update all address fields including street
        onChange(field.name, {
          ...partyValue,
          street: address.street || partyValue.street || '',
          city: address.city || '',
          state: address.state || '',
          postalCode: address.postalCode || '',
          country: address.countryCode || address.country || '',
          placeId: suggestion.place_id,
        });
      }
    }
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (nameInputRef.current && !nameInputRef.current.contains(event.target as Node)) {
        setShowBusinessDropdown(false);
      }
      if (streetInputRef.current && !streetInputRef.current.contains(event.target as Node)) {
        setShowAddressDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleApplySuggestion = () => {
    if (!field.aiSuggestionKey || !enrichmentContext) return;
    const suggestedValue = getNestedValue(enrichmentContext, field.aiSuggestionKey);
    if (suggestedValue) {
      const suggestedParty = parseCompositeValue<PartyValue>(suggestedValue, DEFAULT_PARTY);
      onChange(field.name, suggestedParty);
    }
  };

  // Check if there's a suggestion available
  const hasSuggestion =
    showSuggestion &&
    enrichmentContext &&
    Object.keys(enrichmentContext).length > 0 &&
    getNestedValue(enrichmentContext, field?.aiSuggestionKey || ' ') !== undefined;

  const suggestedParty = hasSuggestion
    ? parseCompositeValue<PartyValue>(
        getNestedValue(enrichmentContext || '', field?.aiSuggestionKey || ''),
        DEFAULT_PARTY
      )
    : null;

  // Check if already applied
  const isApplied =
    suggestedParty &&
    partyValue.name === suggestedParty.name &&
    partyValue.street === suggestedParty.street &&
    partyValue.city === suggestedParty.city &&
    partyValue.state === suggestedParty.state &&
    partyValue.postalCode === suggestedParty.postalCode &&
    partyValue.country === suggestedParty.country;

  // Check if party is empty
  const isEmpty = !partyValue.name && !partyValue.street && !partyValue.city;

  // Show inline button only when field is empty
  const showInlineButton = hasSuggestion && !isApplied && isEmpty;

  // Per-sub-field error: highlight the specific empty fields when there's a validation error
  const nameError = error && !partyValue.name?.trim();
  const streetError =
    error && field.required && !partyValue.street?.trim() && !!partyValue.name?.trim();

  return (
    <fieldset
      className="space-y-3 border-0 p-0 m-0"
      aria-describedby={
        [resolvedHelpText && `${field.name}-help`, error && `${field.name}-error`]
          .filter(Boolean)
          .join(' ') || undefined
      }
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {isBusinessLikely ? (
            <Building2 className="h-4 w-4 text-selise-blue" />
          ) : (
            <User className="h-4 w-4 text-selise-blue" />
          )}
          <legend className="text-sm font-semibold">
            {resolvedLabel}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </legend>
        </div>
        {showSuggestion && (
          <AISuggestionStatusBadge
            suggestionKey={field.aiSuggestionKey || ''}
            enrichmentContext={enrichmentContext}
            currentValue={value}
          />
        )}
      </div>

      {showInlineButton && (
        <button
          type="button"
          onClick={handleApplySuggestion}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md
            bg-selise-blue/5 text-selise-blue 
            hover:bg-selise-blue/15 transition-all cursor-pointer
            border border-selise-blue/20 hover:border-selise-blue/40
            shadow-sm hover:shadow w-fit"
          title="Apply standard party details"
        >
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          <span className="whitespace-nowrap">{t('SMARTFLOW_PARTY_USE_STANDARD_DETAILS')}</span>
        </button>
      )}

      <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/20">
        {/* Name Field - Primary identifier with business search */}
        <div className="space-y-1.5" ref={nameInputRef}>
          <Label
            htmlFor={`${field.name}-name`}
            className="text-xs font-medium text-[hsl(var(--fg))]"
          >
            {isBusinessLikely
              ? t('SMARTFLOW_PARTY_BUSINESS_NAME_LABEL')
              : t('SMARTFLOW_PARTY_FULL_NAME_LABEL')}
          </Label>
          <div className="relative">
            <Input
              id={`${field.name}-name`}
              placeholder={
                suggestedParty?.name ||
                (isBusinessLikely
                  ? t('SMARTFLOW_PARTY_ENTER_COMPANY_PLACEHOLDER')
                  : t('SMARTFLOW_PARTY_ENTER_FULL_NAME_PLACEHOLDER'))
              }
              value={partyValue.name || ''}
              onChange={handleBusinessNameChange}
              onFocus={() => {
                if (isBusinessLikely && partyValue.name && partyValue.name.length >= 2) {
                  setShowBusinessDropdown(true);
                }
              }}
              autoComplete={isBusinessLikely ? 'organization' : 'name'}
              role="combobox"
              aria-expanded={showBusinessDropdown && predictions.length > 0}
              aria-haspopup="listbox"
              aria-controls={`${field.name}-business-listbox`}
              aria-invalid={!!nameError}
              className={cn('font-medium pr-10', nameError ? 'border-destructive' : '')}
            />
            {businessLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-globe-grey" />
              </div>
            )}

            {/* Business Search Dropdown */}
            {showBusinessDropdown && predictions.length > 0 && (
              <div
                role="listbox"
                id={`${field.name}-business-listbox`}
                className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-xl max-h-72 overflow-y-auto"
              >
                <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
                  <p className="text-sm text-globe-grey">
                    {t('SMARTFLOW_ADDRESS_SELECT_BUSINESS')}
                  </p>
                </div>
                {predictions.map((prediction) => (
                  <button
                    key={prediction.place_id}
                    type="button"
                    role="option"
                    onClick={() => handleBusinessSelect(prediction)}
                    className="w-full text-left px-4 py-3 hover:bg-selise-blue/5 transition-colors border-b border-border/30 last:border-b-0"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 p-1 rounded bg-selise-blue/10">
                        <Building2 className="h-3 w-3 text-selise-blue" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-[hsl(var(--fg))]">
                          {prediction.structured_formatting.main_text}
                        </div>
                        <div className="text-sm text-globe-grey mt-0.5 line-clamp-2">
                          {prediction.structured_formatting.secondary_text}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-globe-grey">
            {isBusinessLikely
              ? t('SMARTFLOW_PARTY_LEGAL_NAME_HINT')
              : t('SMARTFLOW_PARTY_FULL_LEGAL_NAME_HINT')}
          </p>
        </div>

        {/* Preview Card - Shows when business is selected */}
        {showPreviewCard && selectedBusiness && (
          <div className="p-4 rounded-lg border-2 border-selise-blue/30 bg-selise-blue/5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-selise-blue" />
                <span className="font-medium text-sm text-[hsl(var(--fg))]">
                  {selectedBusiness.name}
                </span>
              </div>
              <button
                type="button"
                onClick={handleDismissPreview}
                className="text-globe-grey hover:text-[hsl(var(--fg))] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1 text-sm text-globe-grey mb-4">
              {selectedBusiness.address.street && <div>{selectedBusiness.address.street}</div>}
              <div>
                {[
                  selectedBusiness.address.city,
                  selectedBusiness.address.state,
                  selectedBusiness.address.postalCode,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
              {selectedBusiness.address.country && <div>{selectedBusiness.address.country}</div>}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={handleApplyAddress}
                size="sm"
                className="flex-1 bg-selise-blue hover:bg-[hsl(var(--selise-blue))]/90 text-white"
              >
                {t('SMARTFLOW_PARTY_APPLY_ADDRESS')}
              </Button>
              <Button
                type="button"
                onClick={handleDismissPreview}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                {t('SMARTFLOW_PARTY_TYPE_MANUALLY')}
              </Button>
            </div>
          </div>
        )}

        {/* Address Section */}
        <div className="pt-2 border-t border-border/50">
          <p className="text-sm font-medium text-globe-grey mb-3">
            {isBusinessLikely
              ? t('SMARTFLOW_PARTY_REGISTERED_ADDRESS')
              : t('SMARTFLOW_PARTY_ADDRESS')}
          </p>

          {/* Country (full width, at top) */}
          <div className="space-y-1.5 mb-3">
            <Label htmlFor={`${field.name}-country`} className="text-xs text-globe-grey">
              {t('SMARTFLOW_ADDRESS_COUNTRY')}
            </Label>
            <CountrySelect
              id={`${field.name}-country`}
              value={
                partyValue.country && partyValue.country.trim() ? partyValue.country : undefined
              }
              onValueChange={(val) => updateField('country', val)}
              placeholder="Select country"
              error={!!error}
              defaultCountry={detectedCountry ? detectedCountry : 'US'}
            />
          </div>

          {/* Street Address with autocomplete */}
          <div className="space-y-1.5 mb-3" ref={streetInputRef}>
            <Label htmlFor={`${field.name}-street`} className="text-xs text-globe-grey">
              {format.streetLabel}
            </Label>
            <div className="relative">
              <Input
                id={`${field.name}-street`}
                placeholder={suggestedParty?.street || t('SMARTFLOW_ADDRESS_SEARCH_PLACEHOLDER')}
                value={partyValue.street || ''}
                onChange={handleStreetChange}
                onFocus={() => {
                  setStreetFocused(true);
                  if (partyValue.street && partyValue.street.length >= 3) {
                    setShowAddressDropdown(true);
                  }
                }}
                onBlur={() => setStreetFocused(false)}
                autoComplete="address-line1"
                role="combobox"
                aria-expanded={showAddressDropdown && addressSuggestions.length > 0}
                aria-haspopup="listbox"
                aria-controls={`${field.name}-address-listbox`}
                aria-invalid={!!streetError}
                className={cn(streetError ? 'border-destructive' : '')}
              />
              {addressLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-globe-grey" />
                </div>
              )}

              {/* Address Autocomplete Dropdown */}
              {showAddressDropdown && addressSuggestions.length > 0 && (
                <div
                  role="listbox"
                  id={`${field.name}-address-listbox`}
                  className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-xl max-h-72 overflow-y-auto"
                >
                  <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
                    <p className="text-sm text-globe-grey">
                      {t('SMARTFLOW_ADDRESS_SELECT_ADDRESS')}
                    </p>
                  </div>
                  {addressSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.place_id}
                      type="button"
                      role="option"
                      onClick={() => handleAddressSelect(suggestion)}
                      className="w-full text-left px-4 py-3 hover:bg-selise-blue/5 transition-colors border-b border-border/30 last:border-b-0"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 p-1 rounded bg-selise-blue/10">
                          <MapPin className="h-3 w-3 text-selise-blue" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-[hsl(var(--fg))]">
                            {suggestion.structured_formatting?.main_text ||
                              suggestion.description.split(',')[0]}
                          </div>
                          <div className="text-sm text-globe-grey mt-0.5 line-clamp-2">
                            {suggestion.structured_formatting?.secondary_text ||
                              suggestion.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {(streetFocused || !partyValue.street) && (
              <p className="text-xs text-globe-grey">{t('SMARTFLOW_ADDRESS_SEARCH_HINT')}</p>
            )}
          </div>

          {/* City + Postal Code row (order depends on region) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            {format.postalBeforeCity ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor={`${field.name}-postalCode`} className="text-xs text-globe-grey">
                    {format.postalCodeLabel}
                  </Label>
                  <Input
                    id={`${field.name}-postalCode`}
                    placeholder={
                      suggestedParty?.postalCode || t('SMARTFLOW_ADDRESS_POSTAL_CODE_PLACEHOLDER')
                    }
                    value={partyValue.postalCode || ''}
                    onChange={(e) => updateField('postalCode', e.target.value)}
                    autoComplete="postal-code"
                    aria-invalid={false}
                    className=""
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${field.name}-city`} className="text-xs text-globe-grey">
                    {format.cityLabel}
                  </Label>
                  <Input
                    id={`${field.name}-city`}
                    placeholder={suggestedParty?.city || t('SMARTFLOW_ADDRESS_CITY_PLACEHOLDER')}
                    value={partyValue.city || ''}
                    onChange={(e) => updateField('city', e.target.value)}
                    autoComplete="address-level2"
                    aria-invalid={false}
                    className=""
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor={`${field.name}-city`} className="text-xs text-globe-grey">
                    {format.cityLabel}
                  </Label>
                  <Input
                    id={`${field.name}-city`}
                    placeholder={suggestedParty?.city || t('SMARTFLOW_ADDRESS_CITY_PLACEHOLDER')}
                    value={partyValue.city || ''}
                    onChange={(e) => updateField('city', e.target.value)}
                    autoComplete="address-level2"
                    aria-invalid={false}
                    className=""
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${field.name}-postalCode`} className="text-xs text-globe-grey">
                    {format.postalCodeLabel}
                  </Label>
                  <Input
                    id={`${field.name}-postalCode`}
                    placeholder={
                      suggestedParty?.postalCode || t('SMARTFLOW_ADDRESS_POSTAL_CODE_PLACEHOLDER')
                    }
                    value={partyValue.postalCode || ''}
                    onChange={(e) => updateField('postalCode', e.target.value)}
                    autoComplete="postal-code"
                    aria-invalid={false}
                    className=""
                  />
                </div>
              </>
            )}
          </div>

          {/* State / Province (conditional) */}
          {format.showState && (
            <div className="space-y-1.5">
              <Label htmlFor={`${field.name}-state`} className="text-xs text-globe-grey">
                {format.stateLabel}
              </Label>
              <Input
                id={`${field.name}-state`}
                placeholder={suggestedParty?.state || format.stateLabel}
                value={partyValue.state || ''}
                onChange={(e) => updateField('state', e.target.value)}
                autoComplete="address-level1"
                aria-invalid={false}
                className=""
              />
            </div>
          )}
        </div>
      </div>

      {resolvedHelpText && (
        <p id={`${field.name}-help`} className="text-xs text-globe-grey">
          {resolvedHelpText}
        </p>
      )}
      {error && (
        <p id={`${field.name}-error`} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </fieldset>
  );
}

export default PartyField;
